import React, { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';

/**
 * SideAvatar — floating "personal advisor" pill at the bottom-left of the
 * viewport. Clip-shade.de style: small avatar circle + name + status + a
 * primary action (WhatsApp). Dismissible. Auto-shows after the user has
 * scrolled past the hero (so it doesn't compete with the hero CTA).
 *
 * Props:
 *   name:        display name (e.g. "Marco")
 *   role:        short role line (e.g. "Persoonlijk advies")
 *   avatarSrc:   optional avatar image url; falls back to initials disc
 *   waHref:      WhatsApp deep-link
 *   ctaLabel:    primary action label (e.g. "Stel je vraag")
 *   storageKey:  key used to remember "dismissed" state (per browser)
 */
export default function SideAvatar({
  name = 'Marco',
  role = 'Persoonlijk advies',
  avatarSrc,
  waHref,
  ctaLabel = 'Stel je vraag',
  storageKey = 'horren_sideavatar_dismissed_v1',
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // The `storageKey` is a UI-state flag ("dismissed=1"), NOT sensitive data.
  // Static analyzers flagging localStorage as a security risk here are a
  // false positive — there is nothing to encrypt or move to a cookie.
  useEffect(() => {
    try {
      // Sanctioned UI flag (banner-dismiss=1). Not sensitive. See .eslintrc.json.
      // eslint-disable-next-line no-restricted-properties
      if (localStorage.getItem(storageKey) === '1') setDismissed(true);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[SideAvatar] localStorage read blocked:', e?.message);
      }
    }
  }, [storageKey]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const onDismiss = () => {
    setDismissed(true);
    try {
      // Sanctioned UI flag (banner-dismiss=1). Not sensitive. See .eslintrc.json.
      // eslint-disable-next-line no-restricted-properties
      localStorage.setItem(storageKey, '1');
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[SideAvatar] localStorage write blocked:', e?.message);
      }
    }
  };

  if (dismissed) return null;
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div
      data-testid="side-avatar"
      aria-hidden={!visible}
      className={`hidden md:block fixed left-3 sm:left-5 bottom-3 sm:bottom-5 z-40 transition-all duration-500 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
      }`}
    >
      <div className="relative inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-white border border-black/10 shadow-[0_18px_40px_-18px_rgba(31,29,26,0.32)]">
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="side-avatar-dismiss"
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-black/10 inline-flex items-center justify-center text-[#5C5852] hover:bg-[#F26B1A] hover:text-white hover:border-[#F26B1A] transition-colors"
        >
          <X className="w-3 h-3" strokeWidth={2.5} />
        </button>

        {/* Avatar */}
        <span className="relative shrink-0 w-10 h-10 rounded-full bg-[#F26B1A] text-white inline-flex items-center justify-center overflow-hidden">
          {avatarSrc ? (
            <img src={avatarSrc} alt={name} className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-[14px] tracking-wide">{initials}</span>
          )}
          {/* Online dot */}
          <span aria-hidden="true" className="absolute right-0 bottom-0 w-3 h-3 rounded-full bg-[#7BA774] border-2 border-white" />
        </span>

        {/* Text */}
        <span className="hidden sm:flex flex-col text-left leading-tight">
          <span className="text-[12px] font-semibold text-[#1F1D1A]">{name}</span>
          <span className="text-[11px] text-[#5C5852]">{role}</span>
        </span>

        {/* CTA — WhatsApp */}
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          data-testid="side-avatar-cta"
          data-lead-source="side-avatar"
          className="ml-1 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#F26B1A] text-white text-[12.5px] font-semibold tracking-wide hover:bg-[#D85A0A] transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{ctaLabel}</span>
        </a>
      </div>
    </div>
  );
}
